import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useData } from "../../DataContext";
import "bootstrap/dist/css/bootstrap.min.css";
import DateRangePicker from "../date-range-picker/date-range-picker.js";
import FooterContent from "../common/footer-content/footer-content.js";
import HeaderContent from "../common/header-content/header-content.js";
import "./mrt-home-page.css";
import TermsPopup from "../common/dhl-terms-conditions-popup/dhl-terms-conditions-popup.js";

const MrtHomePage = () => {
  const [shipperReference, setShipperReference] = useState("");
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 14);
  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(today);
  const [custcode, setCustCode] = useState("");
  const [showTooltip, setShowTooltip] = useState(false); // State for tooltip visibility
  const navigate = useNavigate();
  const { setTrackingParameters } = useData();
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  useEffect(() => {
    const storedShipperReference = sessionStorage.getItem('shipperReference');
    const storedStartDate = sessionStorage.getItem('startDate');
    const storedEndDate = sessionStorage.getItem('endDate');
    const storedCustCode = sessionStorage.getItem('custcode');

    if (storedShipperReference) {
        setShipperReference(storedShipperReference);
    }
    if (storedStartDate) {
        setStartDate(new Date(storedStartDate.split('/').reverse().join('-')));
    }
    if (storedEndDate) {
        setEndDate(new Date(storedEndDate.split('/').reverse().join('-')));
    }
    if (storedCustCode) {
        setCustCode(storedCustCode);
    }
  }, []);

  const formatDate = (date) => {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    return "";
  };

  const handleTrackShipperReference = (e) => {
    e.preventDefault(); // Prevent default form submission
    sessionStorage.setItem('shipperReference', shipperReference);
    sessionStorage.setItem('startDate', formatDate(startDate));
    sessionStorage.setItem('endDate', formatDate(endDate));
    sessionStorage.setItem('custcode', custcode);

    setTrackingParameters({
      sref: shipperReference
        .split(/[,]+/)
        .map((item) => item.trim())
        .filter((item) => item)
        .join(","),
      fromDate: startDate ? formatDate(startDate) : null,
      toDate: endDate ? formatDate(endDate) : null,
      locale: "en",
      custcode: custcode,
    });
    navigate("/mrt", { state: { source: 'home' } });
  };

  const handleResetButton = () => {
    setShipperReference("");
    setStartDate("");
    setEndDate("");
    setCustCode("");

    // Clear values from sessionStorage
    sessionStorage.removeItem('shipperReference');
    sessionStorage.removeItem('startDate');
    sessionStorage.removeItem('endDate');
    sessionStorage.removeItem('custcode');
  };

  const handleChange = (e) => {
    const input = e.target.value;

    // Count the number of commas and check if the last character is a comma
    const commaCount = (input.match(/,/g) || []).length;

    // Allow input only if there are less than 9 commas or if the last character is a comma when there are 9 commas
    if (commaCount < 9 || commaCount === 9) {
      setShipperReference(input);
    }
  };

  const handleTermsAccept = (accepted) => {
    setIsTermsAccepted(accepted); // Set the checkbox state to true
  };

  // Check if all mandatory fields are filled
  const isAllFieldsFilled = shipperReference.trim() !== "" && custcode.trim() !== "" && startDate && endDate;

  // Check if the button should be enabled
  const isTrackButtonEnabled = isAllFieldsFilled && isTermsAccepted; // The button is enabled only if all fields are filled and terms are accepted

  const isResetButtonEnabled = shipperReference.trim() !== "" || custcode.trim() !== "" || startDate || endDate;

  // Tooltip message logic
  const tooltipMessage = () => {
    if (!isAllFieldsFilled) {
      return "All fields marked with an asterisk (*) are mandatory.";
    }
    if (isAllFieldsFilled && !isTermsAccepted) {
      return "Please accept the terms and conditions.";
    }
    return "";
  };

  return (
    <div className="outer-body-container text-02">
      <HeaderContent toDisplayClientLogo={false} currentPage="mrtHomePage" locale="en" countryCode="gb"/>

      <TermsPopup isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} onAccept={handleTermsAccept} />
      <div className="body-content">
        {/* <div className="column-left"></div> */}
        <div className="box column-middle">
          <div className="contentbody">
            <h1 className="text-01 color-red bold-text">
              Welcome to the DHL Shipment Tracking System.
            </h1>
            <p className="top-gap">
              By using Worldwide Package Tracking, you can track your package at
              any time, day or night, from any location on the World Wide Web.
            </p>
            <p>
              Tracking your order is easy! Simply enter the shipper reference of
              the package you wish to track in the box below.
            </p>

            <div className="custom-checkbox">
              <input
                type="checkbox"
                className="form-check-input"
                id="termsCheckbox"
                checked={isTermsAccepted}
                onChange={(e) => setIsTermsAccepted(e.target.checked)}
                aria-label="DHL Terms and Condition checkBox"
              />
              <label
                className="form-check-label color-red bold-text"
                //htmlFor="termsCheckbox"
              >
                <span>I hereby confirm that I have read the{" "}</span>
                <a
                  className="terms-color"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent default anchor behavior
                    setIsTermsModalOpen(true); // Open the modal
                  }}
                >
                  DHL Express Online Services Terms of Use
                </a>
                <span>{" "}and accept the content thereof.</span>
              </label>
            </div>

            <div className="input-container">
              <div className="container mt-4">
                <form>
                  <div className="row justify-content-center">
                    <div className="col-md-9 removed-padding">
                      <div className="form-group row bottom-gap removed-padding">
                        <label
                          htmlFor="shipper-reference"
                          className="label-column bold-text"
                        >
                          Shipper Reference:
                        </label>
                        <div className="col-sm-9 d-flex align-items-center" style={{ paddingRight: '0px !important'}} >
                          <textarea
                            className="form-control text-area-vertical"
                            id="shipper-reference"
                            value={shipperReference}
                            placeholder="Enter up to 10 numbers, separated by comma"
                            onChange={handleChange}
                          />
                          <span className="mandatory-asterisk asterisk-top">*</span>
                        </div>
                      </div>

                    {/* customer code */}
                      <div className="form-group row margin-gap bottom-gap removed-padding">
                        <div className="col-sm-3">
                          <label
                            htmlFor="custcode"
                            className="label-column bold-text"
                          >
                            Customer Code:
                          </label>
                        </div>
                        <div className="col-sm-9 d-flex align-items-center">
                          <input
                            type="text"
                            className="form-control text-area"
                            id="custcode"
                            value={custcode}
                            onChange={(e) => setCustCode(e.target.value)}
                          />
                          <span className="mandatory-asterisk">*</span>
                        </div>
                      </div>
                      {/* date range */}
                      <div className="form-group row bottom-gap removed-padding" style={{ alignItems: "center" }}>
                        <div className="col-3 text-sm-right">
                          <label className="label-column bold-text">Date Range: </label>
                        </div>
                        <div className="col-9">
                          <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onStartDateChange={setStartDate}
                            onEndDateChange={setEndDate}
                          />
                        </div>
                      </div>

                      {/* buttons */}
                      <div className="form-group row button-container">
                        <div className="col-sm-9 d-flex justify-content-center">
                          <div
                            onMouseEnter={() => 
                              !isTrackButtonEnabled && setShowTooltip(true)
                            }
                            onMouseLeave={() => setShowTooltip(false)}
                          >
                            <button
                              type="button"
                              className="button-66"
                              onClick={handleTrackShipperReference}
                              disabled={!isTrackButtonEnabled}
                            >
                              Start Tracking
                            </button>
                          </div>
                          {(!isTrackButtonEnabled) && showTooltip && (
                            <div className="tooltip-message">
                              {tooltipMessage()}
                            </div>
                          )}
                          <button
                            type="button"
                            className="button-66"
                            onClick={handleResetButton}
                            disabled={!isResetButtonEnabled}
                          >
                            Reset Form
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <br />
            <div className="notice-left">
              <span className="color-red bold-text">Notice: </span>
              <span>
                You are authorised to use this tracking system solely to track
                shipments tendered via DHL Worldwide Express by, for, or to you.
                No other use may be made of the DHL tracking system and
                information without DHL's prior written consent.
              </span>
            </div>
            <br />
          </div>
        </div>
        {/* <div className="column-left"></div> */}
      </div>

      <FooterContent />
    </div>
  );
};

export default MrtHomePage;
